import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../store/admin/vehiclesSocketStore";
import type { Company } from "../../../../store/admin/companiesStore";
import CompanySelector from "../../../../components/base/CompanySelector";
import StatusFilters from "./StatusFilters";
import GroupFilter from "./GroupFilter";
import VehicleList, { useListDensity } from "./VehicleList";
import { UNIT_STATUS_COLORS } from "../../../../constants/unitColors";

interface Group {
  id: string;
  name: string;
  count: number;
}

interface MobileBottomBarProps {
  isDarkMode: boolean;
  unitStats: {
    driving: number;
    ralenti: number;
    stopped: number;
    offline: number;
    total: number;
  };
  vehicles: VehicleSocket[];
  selectedStatus: string | null;
  onStatusClick: (status: string) => void;
  setSelectedStatus: (status: string | null) => void;
  groups: Group[];
  selectedGroup: string;
  setSelectedGroup: (group: string) => void;
  isReseller: boolean;
  companies: Company[];
  selectedCompany: string | null;
  onCompanyChange?: (companyId: string | null) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchClear?: () => void;
  searchPlaceholder?: string;
  searchResultCount?: number;
  searchTotalCount?: number;
  loading?: boolean;
  isConnected?: boolean;
  themeClasses: {
    bg: string;
    border: string;
    text: string;
    textSecondary: string;
    cardBg: string;
    cardBorder: string;
    buttonHover: string;
    inputBg?: string;
    inputBorder?: string;
  };
}

export default function MobileBottomBar({
  isDarkMode,
  unitStats,
  vehicles,
  selectedStatus,
  onStatusClick,
  setSelectedStatus,
  groups,
  selectedGroup,
  setSelectedGroup,
  isReseller,
  companies,
  selectedCompany,
  onCompanyChange,
  searchQuery,
  onSearchChange,
  onSearchClear,
  searchPlaceholder,
  searchResultCount,
  searchTotalCount,
  loading = false,
  isConnected = true,
  themeClasses,
}: MobileBottomBarProps) {
  const { t } = useTranslation(["lite", "common"]);
  const [isExpanded, setIsExpanded] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar || isExpanded) return;
    const publishHeight = () =>
      document.documentElement.style.setProperty(
        "--lite-bottom-bar-h",
        `${bar.offsetHeight}px`,
      );
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [isExpanded]);

  useEffect(
    () => () => {
      document.documentElement.style.removeProperty("--lite-bottom-bar-h");
    },
    [],
  );


  const selectedCompanyName = selectedCompany
    ? companies.find((c) => c.id === selectedCompany)?.name ||
      companies.find((c) => c.id === selectedCompany)?.business_name ||
      selectedCompany
    : null;

  const stats = [
    {
      id: "driving",
      count: unitStats.driving,
      color: UNIT_STATUS_COLORS.driving,
      bgColor: "bg-primary-500",
      animate: true,
      label: t("status.driving"),
    },
    {
      id: "ralenti",
      count: unitStats.ralenti,
      color: UNIT_STATUS_COLORS.ralenti,
      bgColor: "bg-amber-400",
      animate: false,
      label: t("status.ralenti"),
    },
    {
      id: "stopped",
      count: unitStats.stopped,
      color: UNIT_STATUS_COLORS.stopped,
      bgColor: "bg-red-400",
      animate: false,
      label: t("status.stopped"),
    },
    {
      id: "offline",
      count: unitStats.offline,
      color: UNIT_STATUS_COLORS.offline,
      bgColor: "bg-gray-400",
      animate: false,
      label: t("status.offline"),
    },
  ];

  const [density, setDensity] = useListDensity();

  // Extended themeClasses for StatusFilters and GroupFilter
  const extendedThemeClasses = {
    ...themeClasses,
    inputBg: themeClasses.inputBg || (isDarkMode ? "bg-dark-card" : "bg-gray-100"),
    inputBorder: themeClasses.inputBorder || (isDarkMode ? "border-dark-border" : "border-gray-300"),
  };

  return (
    <div
      ref={barRef}
      className={`
        fixed bottom-0 left-0 right-0 z-40
        ${themeClasses.bg} 
        border-t ${themeClasses.border}
        shadow-lg
        transition-all duration-300 ease-in-out
        ${isExpanded ? "h-[calc(100vh-52px)]" : "h-auto"}
        flex flex-col
      `}
    >
      {/* Expanded Content - Same layout as desktop sidebar */}
      {isExpanded && (
        <>
          {/* Header */}
          <div className={`p-3 border-b ${themeClasses.border} flex-shrink-0`}>
            <div className="flex items-center justify-between mb-3">
              <h2 className={`text-base font-bold ${themeClasses.text}`}>
                {t("sidebar.title")}
              </h2>
              <div className="flex items-center space-x-2">
                {selectedStatus && (
                  <button
                    onClick={() => setSelectedStatus(null)}
                    className="px-2 py-1 text-xs bg-red-500/10 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors flex items-center space-x-1"
                  >
                    <i className="ri-close-line text-xs"></i>
                    <span>{t("sidebar.clearFilter")}</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setDensity(density === "compact" ? "detailed" : "compact")}
                  className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                  title={t(density === "compact" ? "compactList.showDetailed" : "compactList.showCompact")}
                  aria-label={t(density === "compact" ? "compactList.showDetailed" : "compactList.showCompact")}
                >
                  <i
                    className={`${density === "compact" ? "ri-layout-row-line" : "ri-list-check"} text-primary-500 text-sm`}
                  ></i>
                </button>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                  title={t("sidebar.collapseSidebar")}
                  aria-label={t("sidebar.collapseSidebar")}
                  aria-expanded
                >
                  <i className="ri-arrow-down-s-line text-primary-500 text-sm"></i>
                </button>
              </div>
            </div>

            {/* Company selector for resellers */}
            {isReseller && onCompanyChange && (
              <div className="mb-3">
                <CompanySelector
                  value={selectedCompany}
                  onChange={onCompanyChange}
                  isDarkMode={isDarkMode}
                  isClearable
                  companies={companies.length > 0 ? companies : undefined}
                />
              </div>
            )}

            {/* Company indicator for resellers (solo lectura si no hay onCompanyChange) */}
            {isReseller && !onCompanyChange && (
              <div
                className={`mb-3 p-2.5 rounded-xl border ${
                  selectedCompany
                    ? "bg-primary-500/10 border-primary-500/30"
                    : isDarkMode
                      ? "bg-dark-card border-dark-border"
                      : "bg-gray-100 border-gray-200"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <i
                    className={`ri-building-2-line text-base ${
                      selectedCompany
                        ? "text-primary-500"
                        : themeClasses.textSecondary
                    }`}
                  ></i>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-xs font-medium ${
                        selectedCompany
                          ? "text-primary-500"
                          : themeClasses.textSecondary
                      }`}
                    >
                      {selectedCompany
                        ? t("sidebar.companySelected")
                        : t("sidebar.noCompanySelected")}
                    </p>
                    {selectedCompany && selectedCompanyName && (
                      <p
                        className={`text-sm font-semibold ${themeClasses.text} truncate`}
                      >
                        {selectedCompanyName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <StatusFilters
              selectedStatus={selectedStatus}
              onStatusClick={onStatusClick}
              onClear={() => setSelectedStatus(null)}
              unitStats={unitStats}
              themeClasses={extendedThemeClasses}
            />

            <GroupFilter
              selectedGroup={selectedGroup}
              onGroupChange={setSelectedGroup}
              groups={groups}
              themeClasses={extendedThemeClasses}
              isDarkMode={isDarkMode}
              isReseller={isReseller}
              selectedCompany={selectedCompany}
            />

            {/* Buscador de unidades (en móvil el header no lo muestra) */}
            {onSearchChange && (
              <div className="relative mt-3">
                <i
                  className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
                ></i>
                <input
                  type="text"
                  value={searchQuery || ""}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={
                    searchPlaceholder || t("common:header.searchPlaceholder")
                  }
                  className={`w-full pl-10 pr-16 py-2 ${extendedThemeClasses.inputBg} border ${extendedThemeClasses.inputBorder} rounded-lg ${themeClasses.text} placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm`}
                />
                {searchQuery && (
                  <button
                    aria-label={t("general.close", { ns: "common" })}
                    onClick={onSearchClear}
                    className={`absolute right-12 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} hover:text-red-500`}
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
                {searchResultCount !== undefined && (
                  <span
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${themeClasses.textSecondary}`}
                  >
                    {searchResultCount}/{searchTotalCount}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Vehicle List */}
          <div className={`flex-1 overflow-y-auto ${density === "compact" ? "p-2 space-y-0.5" : "p-3 space-y-2.5"}`}>
            {loading ? (
              <div className="space-y-2.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.cardBorder} p-3 animate-pulse`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-10 h-10 rounded-lg ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
                      />
                      <div className="flex-1 space-y-2">
                        <div
                          className={`h-4 rounded ${isDarkMode ? "bg-gray-600" : "bg-gray-300"} w-3/4`}
                        />
                        <div
                          className={`h-3 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} w-1/2`}
                        />
                      </div>
                      <div
                        className={`w-16 h-6 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : vehicles.length > 0 ? (
              <>
                {!isConnected && (
                  <div className="mb-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center space-x-2">
                    <i className="ri-wifi-off-line text-yellow-500"></i>
                    <span className="text-xs text-yellow-500">
                      {t("sidebar.reconnecting")}
                    </span>
                  </div>
                )}
                <VehicleList
                  vehicles={vehicles}
                  density={density}
                  isDarkMode={isDarkMode}
                  themeClasses={extendedThemeClasses}
                  selectedStatus={selectedStatus}
                  onFocus={() => setIsExpanded(false)}
                />
              </>
            ) : isReseller && !selectedCompany ? (
              <div className="flex flex-col items-center justify-center h-full px-6 py-8">
                <div className={`w-14 h-14 rounded-full ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} flex items-center justify-center mb-3`}>
                  <i className={`ri-building-2-line text-xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
                </div>
                <p className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} text-center mb-1`}>
                  {t("sidebar.selectCompanyFirst")}
                </p>
                <p className={`text-xs ${themeClasses.textSecondary} text-center`}>
                  {t("sidebar.selectCompanyHint")}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full px-6 py-8">
                <div className={`w-14 h-14 rounded-full ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} flex items-center justify-center mb-3`}>
                  <i className={`ri-car-line text-xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
                </div>
                <p className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} text-center`}>
                  {t("sidebar.noUnits")}
                </p>
              </div>
            )}
          </div>

        </>
      )}

      {/* Collapsed Bar Content */}
      {!isExpanded && (
        <div className="px-3 pb-2">
          {/* Agarradera y título también abren la lista: antes sólo la flecha
              de ~30 px, y tocar «Control de Flota» no hacía nada (prueba en
              390 px). Es el gesto que se espera de una hoja inferior. */}
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            aria-label={t("sidebar.expandSidebar")}
            className="w-full flex justify-center pt-2 pb-1"
          >
            <span aria-hidden className={`h-1 w-10 rounded-full ${isDarkMode ? "bg-white/20" : "bg-black/15"}`} />
          </button>
          {/* Header Row */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              aria-expanded={false}
              className="flex flex-1 min-w-0 min-h-[40px] items-center space-x-2 text-left"
            >
              <h3 className={`text-sm font-bold ${themeClasses.text}`}>
                {t("sidebar.title")}
              </h3>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${themeClasses.cardBg} ${themeClasses.text}`}
              >
                {vehicles.length}
              </span>
            </button>
            <div className="flex items-center space-x-2">
              {selectedStatus && (
                <button
                  onClick={() => setSelectedStatus(null)}
                  className="px-2 py-1 text-xs bg-red-500/10 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors flex items-center space-x-1"
                >
                  <i className="ri-close-line text-xs"></i>
                  <span>{t("sidebar.clearFilter")}</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                title={t("sidebar.expandSidebar")}
                aria-label={t("sidebar.expandSidebar")}
                aria-expanded={false}
                className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              >
                <i className="ri-arrow-up-s-line text-primary-500 text-lg"></i>
              </button>
            </div>
          </div>

          {isReseller && onCompanyChange && (
            <div className="mb-2">
              <CompanySelector
                value={selectedCompany}
                onChange={onCompanyChange}
                isDarkMode={isDarkMode}
                isClearable
                companies={companies.length > 0 ? companies : undefined}
                menuPlacement="top"
              />
            </div>
          )}

          {/* Status Filters - 2x2 en móvil, 4x1 desde tablet */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 w-full">
            {stats.map((stat) => (
              <button
                key={stat.id}
                onClick={() => onStatusClick(stat.id)}
                className={`
                  flex items-center justify-center min-w-0
                  px-2 py-1.5
                  rounded-lg border transition-all
                  ${
                    selectedStatus === stat.id
                      ? "border-2"
                      : `${themeClasses.cardBg} border ${themeClasses.cardBorder}`
                  }
                `}
                style={{
                  backgroundColor:
                    selectedStatus === stat.id ? `${stat.color}20` : undefined,
                  borderColor:
                    selectedStatus === stat.id ? stat.color : undefined,
                }}
              >
                <div className="flex items-center space-x-1.5 min-w-0">
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${stat.animate ? "animate-pulse" : ""}`}
                    style={{ backgroundColor: stat.color }}
                  />
                  <span className={`text-xs ${themeClasses.textSecondary} truncate`}>
                    {stat.label}
                  </span>
                  <span
                    className="text-sm font-bold flex-shrink-0"
                    style={{ color: stat.color }}
                  >
                    {stat.count}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
